import { api } from "../api/client.js";
import { providerBody } from "../epg/api.js";
import { useChannelHealthStore, type ProbeOutcome, type ProbeResult } from "./channelHealthStore.js";

/**
 * One client-side health cycle (Section 12). Picks a bounded, HLS-only set of
 * channels, asks the server to probe their manifests (server-side so provider
 * URLs never leave the server's fetch context — CORS-safe), and feeds the raw
 * results to the health store's flip policy. Best-effort: any failure just
 * skips the cycle — health is a background nicety, never a hard failure.
 */

export interface ProbeTarget {
  channelId: string;
  url: string;
}

/** Only HLS channels can be manifest-probed; raw-TS / native URLs are skipped. */
export function isProbeableChannel(url: string): boolean {
  return url.toLowerCase().includes(".m3u8");
}

function normalizeUrl(url: string): string {
  try {
    return new URL(url).href;
  } catch {
    return url;
  }
}

/**
 * Match raw probe results back to the targets that asked for them.
 *
 * CONTRACT: the server echoes the ORIGINAL (redacted) URL it was sent — never
 * the hydrated one with real credentials. The catalog hands the client only
 * redacted URLs (`/live/[redacted]/[redacted]/…`), so a result keyed by any
 * other form would never match and the whole health cycle would silently
 * record nothing (the bug this helper exists to prevent).
 */
export function matchProbeResults(
  targets: ProbeTarget[],
  results: ProbeResult[],
): Array<{ channelId: string; result: ProbeResult }> {
  const byUrl = new Map(results.map((r) => [normalizeUrl(r.url), r]));
  const pairs: Array<{ channelId: string; result: ProbeResult }> = [];
  for (const target of targets) {
    const result = byUrl.get(normalizeUrl(target.url));
    if (result) pairs.push({ channelId: target.channelId, result });
  }
  return pairs;
}

const NOTHING_PROBED: ProbeOutcome = { probedIds: [], skipped: 0, retryAfterSeconds: 0 };

/**
 * One health request. Returns what the cycle managed to do — how many channels were really
 * probed, how many the account's allowance could not pay for, and how long until it can pay
 * again — so the caller can pace the next cycle (the server names the number; see ProbeOutcome).
 * Best-effort: a failure behaves like "nothing was probed", which paces nothing.
 */
let probesInFlight = 0;

/** How many health requests are out right now — for the debug server's `busy`. */
export function healthProbesInFlight(): number {
  return probesInFlight;
}

export async function runHealthProbe(targets: ProbeTarget[]): Promise<ProbeOutcome> {
  if (targets.length === 0) return NOTHING_PROBED;
  probesInFlight++;
  try {
    return await probeOnce(targets);
  } finally {
    probesInFlight--;
  }
}

async function probeOnce(targets: ProbeTarget[]): Promise<ProbeOutcome> {
  try {
    const data = await api.post<{ results: ProbeResult[]; skipped?: number; retryAfterSeconds?: number }>(
      "/api/health/probe",
      {
        channelUrls: targets.map((t) => t.url),
        ...providerBody(),
      },
    );
    const pairs = matchProbeResults(targets, data.results);
    useChannelHealthStore.getState().recordProbeResults(pairs);
    const outcome: ProbeOutcome = {
      probedIds: pairs.map((pair) => pair.channelId),
      // The server drops skipped channels from `results`; its own count is authoritative when present.
      skipped: data.skipped ?? Math.max(0, targets.length - data.results.length),
      retryAfterSeconds: data.retryAfterSeconds ?? 0,
    };
    useChannelHealthStore.getState().noteProbeOutcome(outcome);
    return outcome;
  } catch {
    // Health is best-effort — never break a browsing session on a probe error.
    return NOTHING_PROBED;
  }
}
